/**
 * Recently accessed properties under the Properties item in the left nav.
 *
 * Legacy `navigation-sidebar.component.html` lists up to five buildings under
 * Properties (label MOST RECENT) and hides the list for Limited User and when
 * it is empty. A failed load stays empty, same as `myaccess.js` and FR-125:
 * do not paint the unscoped local cache.
 */
import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/api/client';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/cn';
import { isLimitedUser } from '@/lib/permissions';
import {
  loadRecentsCache,
  mergeRecents,
  type RecentAccessedRow,
} from '@/features/properties/lib/recent-properties';

const SIDEBAR_RECENTS_LIMIT = 5;

type SidebarRecentPropertiesProps = {
  /** False on the collapsed desktop rail. The list needs the expanded labels. */
  expanded: boolean;
  /** Report-review swaps this to the company-manager role. Limited User is hidden. */
  role: string | null | undefined;
  /** Prefixes property links when Report Review is scoping the nav. */
  toPath: (path: string) => string;
};

/**
 * Compact recent-property list rendered directly under Properties.
 */
export function SidebarRecentProperties({
  expanded,
  role,
  toPath,
}: SidebarRecentPropertiesProps) {
  const userId = useAuthStore((s) => s.user?.userId);
  const [localCache] = useState<RecentAccessedRow[]>(() => loadRecentsCache(userId));
  const { data, isSuccess, isError } = useQuery({
    queryKey: ['recent-properties', userId],
    queryFn: () => apiGet<RecentAccessedRow[]>(`/users/${userId}/recent-properties`),
    enabled: expanded && !isLimitedUser(role ?? '') && !!userId,
  });

  const rows = useMemo(() => {
    if (!isSuccess || isError) return [];
    return mergeRecents(Array.isArray(data) ? data : [], localCache, true)
      .filter((row) => row.buildingName.trim().length > 0)
      .slice(0, SIDEBAR_RECENTS_LIMIT);
  }, [data, isError, isSuccess, localCache]);

  if (!expanded || isLimitedUser(role ?? '') || rows.length === 0) return null;

  return (
    <ul aria-label="Recently accessed" className="mb-1 ml-7 mt-0.5 flex flex-col gap-0.5">
      <li className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-sidebar-text">
        Recently accessed
      </li>
      {rows.map((row) => (
        <li key={row.buildingId}>
          <NavLink
            to={toPath(`/properties/${row.buildingId}`)}
            className={cn(
              'block truncate rounded-lg px-3 py-1 text-xs font-medium text-sidebar-text transition-colors duration-[var(--duration-fast)] hover:bg-sidebar-hover hover:text-sidebar-text-active',
            )}
            title={row.buildingName}
          >
            {row.buildingName}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
